import { isNil } from 'ramda'
import { useCallback, useEffect, useState } from 'react'

import { get } from '../../utils/api'
import { handleApiErrors } from '../../utils/apiErrors'
import {
  addResult,
  mergeResults,
  removeResult,
  updateResult,
} from '../../utils/pagination'
import { getQueryString } from '../../utils/queryString'
import { getObservationsQueryParams } from './getObservationsQueryParams'
import {
  ObservationHandler,
  ObservationsParams,
  ObservationsResult,
} from './types'

const itemsPerPage = 20

export const useFetchObservations = (params: ObservationsParams) => {
  const [isFetching, setFetching] = useState(false)
  const [observations, setObservations] = useState<
    ObservationsResult | undefined
  >(undefined)

  const limit = itemsPerPage
  const [offset, setOffset] = useState(0)

  const loadObservations = useCallback(
    async (page?: number) => {
      const currentOffset = isNil(page) ? offset : page * limit
      const nextOffset = isNil(page) ? offset + limit : (page + 1) * limit

      const querystring = getQueryString({
        ...{ limit, offset: currentOffset },
        ...getObservationsQueryParams(params),
      })

      setFetching(true)

      get<ObservationsResult>(`/observations/?${querystring}`)
        .then((response) => setObservations(mergeResults(response)))
        .then(() => setOffset(nextOffset))
        .catch(handleApiErrors)
        .finally(() => setFetching(false))
    },
    [offset, limit, params]
  )

  const addObservation: ObservationHandler = (o) =>
    setObservations(addResult(o))

  const removeObservation: ObservationHandler = (o) =>
    setObservations(removeResult(o))

  const updateObservation: ObservationHandler = (o) =>
    setObservations(updateResult(o))

  useEffect(() => {
    setObservations(undefined)
    setOffset(0)
  }, [
    // We need to explicitly set useEffect dependencies because objects
    // are compared with reference equality instead of deep equality.
    // https://stackoverflow.com/q/54095994
    params.productId,
    params.trainingId,
    params.trainingTestId,
    params.availableForTesting,
    params.annotationClasses,
    params.productClasses,
    params.filename,
  ])

  return {
    observations,
    isFetching,
    loadObservations,
    addObservation,
    removeObservation,
    updateObservation,
  }
}
